/**
 * Information pages (docs/phase2-plan.md workstream 5, inbox 4): privacy, disclaimer, accessibility statement and terms
 * of use. Privacy, accessibility and terms are DRAFTS pending legal review and say so (DRAFT_PAGES). The structure is here;
 * every text is an i18n key (`page.<name>.<block>`), so each language has the same blocks.
 */

import { REPORT_EMAIL } from '../diagnostics/buildInfo';
import { COUNTER_URL, counterKey } from './counter';

export const PAGE_NAMES = ['privacy', 'disclaimer', 'accessibility', 'terms'] as const;
export type PageName = (typeof PAGE_NAMES)[number];

/** `email`: the text, then a mailto link to CONTACT_EMAIL. `link`: the text as a link to the licence texts (LICENCES_FILE). */
/** `optOut`: the "Don't count my checks" switch (its label and help are `<key>` and `<key>Help`). */
export type Block = { tag: 'h2' | 'p' | 'li' | 'email' | 'optOut' | 'link'; key: string };

/** The one contact channel (online-only service: no phone line or postal address). */
export const CONTACT_EMAIL = REPORT_EMAIL;

/** The licence texts of the bundled fonts and icons (public/licences.txt; precached, so it opens offline). */
export const LICENCES_FILE = './licences.txt';

/** The text for a build with the usage counter, when it differs (`<key>Counter`). */
const withCounter = (key: string) => counterKey(key);
const blocks = (page: PageName, spec: [Block['tag'], string][]): Block[] => spec.map(([tag, key]) => ({ tag, key: `page.${page}.${key}` }));

export const PAGES: Record<PageName, Block[]> = {
  privacy: blocks('privacy', [
    ['p', withCounter('intro')],
    // A build with the counter says what the counter changes (claims register, 2026-10-18): the *Counter variants.
    ['h2', 'deviceHeading'], ['p', 'device'], ['li', withCounter('deviceUpload')], ['li', withCounter('deviceNoServer')], ['li', withCounter('deviceNoTracking')],
    // Only in a build with the usage counter (src/ui/counter.ts): exactly what it sends, and the switch that stops it
    // (Matt, 2026-10-18; docs/claims-register.md). The heading is the target of "What exactly is sent?" (#/privacy/counter).
    ...(COUNTER_URL ? ([['h2', 'counterHeading'], ['p', 'counterIntro'], ['p', 'counterWhen'], ['p', 'counterRead'], ['p', 'counterNothingElse'], ['p', 'counterIp'], ['optOut', 'counterOptOut']] as [Block['tag'], string][]) : []),
    ['h2', 'storedHeading'], ['li', withCounter('storedLanguage')],
    // Brand C (production since 2026-10-18) has the theme control (src/ui/theme.ts): its choice is kept too (claims register, 2026-10-18).
    ...(__BRAND__ === 'c' ? ([['li', 'storedTheme']] as [Block['tag'], string][]) : []),
    ['li', 'storedMemory'], ['li', 'storedIdNumber'], ['li', 'storedOffline'], ['li', 'offline'], ['li', 'offlinePhotos'],
    // "Do I need to install the app?" (Matt, 2026-10-27): a closed fold in design D (src/ui/flowD/tvPrivacyModel.ts `install` card).
    ['h2', 'installHeading'], ['p', 'installNo'], ['p', 'installWhat'], ['p', 'installOffline'],
    ['h2', 'reportHeading'], ['p', 'report'], ['p', 'reportSendOnly'], ['p', 'reportContains'],
    ['li', 'reportVersion'], ['li', 'reportInput'], ['li', 'reportVendor'], ['li', 'reportFiles'], ['li', 'reportTechnical'], ['li', 'reportYear'], ['li', 'reportFields'],
    ['li', 'reportReconciliation'], ['li', 'reportFindings'], ['li', 'reportLabels'], ['li', 'reportFeedback'], ['li', 'reportErrors'], ['li', 'reportComment'], ['li', 'reportLayout'],
    ['p', 'reportNever'],
    // The Share button (src/share; MATT GO 2026-10-25 01:06): it sends nothing to us (claims register).
    ['p', 'share'], ['p', 'sourceLinks'],
    // Opt-in error reports: every build (src/beta/errorReport.ts; docs/plans/error-reports.md; MATT GO 2026-10-26 04:58); the
    // "Report a problem on this page" line only where that link is (preview builds).
    ...(__ERROR_REPORTS__ ? ([['h2', 'reportOnErrorHeading'], ['p', 'reportOnError'], ['p', 'reportOnErrorContains'], ...(__BETA__ ? [['p', 'reportOnErrorProblem']] : [])] as [Block['tag'], string][]) : []),
    // The "Please check" note to payroll: preview builds only (src/results/note.ts; stripped from production like the beta aids' texts).
    ...(__BETA__ ? ([['h2', 'noteHeading'], ['p', 'note']] as [Block['tag'], string][]) : []),
    // Only in a build with the cover-your-details tool (src/cover; vite.config.ts `__COVER__`).
    ...(__COVER__ ? ([['h2', 'coverHeading'], ['p', 'cover'], ['p', 'coverContains'], ['p', 'coverStays'], ['p', 'coverMiss']] as [Block['tag'], string][]) : []),
    ['h2', 'hostingHeading'], ['p', 'hosting'], ['p', 'hostingReport'],
    ['h2', 'contactHeading'], ['email', 'contact'], ['p', 'contactByEmail'],
  ]),
  // Final text (Matt, 2026-10-09), no longer a draft.
  // "Your files never leave your device" added for the launch (inbox 4, 2026-10-16).
  disclaimer: blocks('disclaimer', [['p', 'independent'], ['p', 'compares'], ['p', 'worthChecking'], ['p', 'onDevice'], ['p', 'mistakes']]),
  accessibility: blocks('accessibility', [
    ['p', 'intro'],
    ['h2', 'standardHeading'], ['p', 'standard'],
    ['h2', 'featuresHeading'], ['li', 'featureKeyboard'], ['li', 'featureScreenReader'], ['li', 'featureLanguages'], ['li', 'featureContrast'],
    ['li', 'featureZoom'], ['li', 'featureNoColourOnly'], ['li', 'featureForms'],
    ['h2', 'limitsHeading'], ['li', 'limitNativeControls'], ['li', 'limitPdf'], ['li', 'limitManualPass'],
    ['h2', 'contactHeading'], ['p', 'contact'], ['p', 'coordinator'], ['email', 'contactEmail'], ['p', 'contactByEmail'],
    ['p', 'updated'],
  ]),
  // Draft for the launch (inbox 4, 2026-10-16), pending a lawyer's review (docs/launch-checklist.md).
  terms: blocks('terms', [
    ['p', 'intro'],
    ['h2', 'whatHeading'], ['p', 'what'], ['p', 'minimums'], ['p', 'noAdvice'],
    ['h2', 'useHeading'], ['p', 'worthChecking'], ['p', 'yourDecision'], ['p', 'ownDocuments'],
    ['h2', 'privacyHeading'], ['p', withCounter('onDevice')],
    ['h2', 'betaHeading'], ['p', 'beta'], ['p', 'liability'],
    ['h2', 'creditsHeading'], ['p', 'credits'], ['li', 'creditsLucide'], ['li', 'creditsRubik'], ['li', 'creditsPlex'], ['link', 'creditsTexts'],
    ['h2', 'changesHeading'], ['p', 'changes'],
    ['h2', 'contactHeading'], ['email', 'contact'],
    ['p', 'updated'],
  ]),
};

/** Pages still waiting for legal review: they show the draft notice. */
export const DRAFT_PAGES: readonly PageName[] = ['privacy', 'accessibility', 'terms'];

export const isPageName = (s: string): s is PageName => (PAGE_NAMES as readonly string[]).includes(s);

/** `#/privacy` → 'privacy'; anything else → undefined (the app). */
export function pageFromHash(hash: string): PageName | undefined {
  // A section after the page's name (#/privacy/counter) opens the page; the app moves to the section.
  const name = hash.replace(/^#\/?/, '').split('/')[0] ?? '';
  return isPageName(name) ? name : undefined;
}
